<template>
  <section v-if="groups.length" class="home-summary">
    <h5 class="font-weight-bold mb-3">Workspace</h5>

    <!-- one card, one line a figure: the numbers are there to be scanned, and a row of
         boxed tiles each shouting its own number was the noise on this screen -->
    <b-card no-body class="home-ws">
      <div v-for="group in groups" :key="group.key" class="home-ws__group">
        <div class="home-ws__title">
          {{ group.title }}
          <span v-if="group.note" class="home-ws__note">{{ group.note }}</span>
        </div>
        <component
          :is="card.to ? 'router-link' : 'div'"
          v-for="card in group.cards"
          :key="card.key"
          :to="card.to"
          :title="card.linkLabel"
          class="home-ws__row"
        >
          <span class="home-ws__name">{{ card.label }}</span>
          <span class="home-ws__value">{{ card.value }}</span>
          <span v-if="card.context.length" class="home-ws__context">
            <template v-for="(part, i) in card.context">
              <span v-if="i" :key="`sep-${card.key}-${i}`" class="mx-1">&middot;</span>
              <span :key="`part-${card.key}-${i}`" :class="{ 'home-ws__alert': part.alert }">{{ part.text }}</span>
            </template>
          </span>
        </component>
      </div>
    </b-card>
  </section>
</template>

<script>
import dayjs from 'dayjs'
import { mapActions } from 'vuex'
import { lastWeekWindow, rangeLabel } from './utils'
import { SERVER_DATE_TIME_FORMAT } from '@/components/desk/utils'

const DATE_LABEL_FORMAT = 'MMM D, YYYY'
// the hour ahead that "free right now" means - long enough to walk to the desk
const FREE_WINDOW_HOURS = 1

export default {
  name: 'HomeSummary',
  data: () => ({
    // only the sections this reader is allowed come back, so the cards follow the answer
    summary: {},
    // deliberately outside the cached summary: it goes stale in minutes
    freeNow: null,
    window: lastWeekWindow()
  }),
  computed: {
    /** The period the cards report, in the shape the bookings screen reads back. */
    weekQuery() {
      return {
        since: this.window.from.format(SERVER_DATE_TIME_FORMAT),
        until: this.window.to.format(SERVER_DATE_TIME_FORMAT)
      }
    },
    /**
     * The week's cards, in the order the screen reads them.
     *
     * Each card is built only when its section arrived: a section the reader may not see is
     * never computed on the server, so its absence is the permission check.
     */
    weekCards() {
      const { bookings, confirmations, busiest_floors: busiest } = this.summary
      const cards = []

      if (bookings) {
        cards.push({
          key: 'bookings',
          label: 'Bookings',
          value: bookings.total,
          context: [{ text: `${bookings.single} one-off` }, { text: `${bookings.recurring} recurring` }],
          // the list opens on the same week this card counted, not on its own default month
          to: { name: 'bookings', query: this.weekQuery },
          linkLabel: 'All bookings'
        })
      }

      if (confirmations) {
        cards.push({
          key: 'confirmations',
          label: 'Confirmations',
          value: `${confirmations.confirmed} of ${confirmations.total}`,
          context: [
            { text: `${confirmations.cancelled} cancelled` },
            { text: `${confirmations.auto_released} auto-released` }
          ],
          to: { name: 'settings', query: { tab: 'booking' } },
          linkLabel: 'Booking settings'
        })
      }

      if (busiest) {
        cards.push({
          key: 'busiest_floors',
          label: 'Busiest floors',
          // the leader as the figure, the ranking beneath it
          value: busiest.length ? busiest[0].name : '—',
          context: busiest.length
            ? [{ text: busiest.map((floor) => `${floor.name} — ${floor.bookings}`).join(' · ') }]
            : [{ text: 'Nothing was booked last week' }],
          // the same three floors, over the same week: the card is a ranking, and the link
          // is that ranking spelled out row by row
          to: {
            name: 'bookings',
            query: { ...this.weekQuery, floor: busiest.map((floor) => floor.floor_id) }
          },
          linkLabel: 'These floors in the list'
        })
      }

      return cards
    },
    nowCards() {
      const { desks, employees, floors, inventory, users, integrations } = this.summary
      const cards = []

      if (desks) {
        cards.push({
          key: 'desks',
          label: 'Desks',
          value: desks.total,
          context: [
            { text: `${desks.assigned} permanently assigned` },
            // absent while the count is still on its way, or if it failed on its own
            ...(this.freeNow === null ? [] : [{ text: `${this.freeNow} free right now` }])
          ],
          // straight to the tab that can answer it, rather than to the list of what is taken
          to: { name: 'bookings', query: { tab: 'free' } },
          linkLabel: 'Find a free desk'
        })
      }

      if (employees) {
        cards.push({
          key: 'employees',
          label: 'Employees',
          value: employees.total,
          context: [
            // a person nobody has given a desk is the one thing on this card worth acting on
            { text: `${employees.unplaced} not on a map`, alert: employees.unplaced > 0 },
            // whichever of an import or a SCIM sync touched the directory last
            { text: this.directoryLine(employees) }
          ],
          to: { name: 'employees' },
          linkLabel: 'All employees'
        })
      }

      if (floors) {
        cards.push({
          key: 'floors',
          label: 'Floors and maps',
          value: `${floors.published} published`,
          context: [
            { text: `${floors.drafts} drafts` },
            { text: `${floors.without_plan} without a plan`, alert: floors.without_plan > 0 }
          ],
          to: { name: 'floors' },
          linkLabel: 'All floors'
        })
      }

      if (inventory) {
        const parts = [
          ['rooms', 'rooms'],
          ['printers', 'printers'],
          ['pcs', 'PCs'],
          ['areas', 'areas']
        ]
        cards.push({
          key: 'inventory',
          label: 'On the maps',
          value: inventory.total,
          // only the kinds this reader may see came back, and the total is theirs to match
          context: parts
            .filter(([key]) => inventory[key] !== undefined)
            .map(([key, noun]) => ({ text: `${inventory[key]} ${noun}` }))
        })
      }

      if (users) {
        cards.push({
          key: 'users',
          label: 'Users',
          value: `${users.active} active`,
          context: [{ text: `+${users.created} last week` }, { text: `${users.scim} from SCIM` }],
          to: { name: 'users' },
          linkLabel: 'All users'
        })
      }

      if (integrations) {
        const parts = this.integrationParts(integrations)
        const pending = parts.find((part) => part.alert)
        cards.push({
          key: 'integrations',
          label: 'Integrations',
          value: `${parts.filter((part) => part.on).length} of ${parts.length} on`,
          context: parts,
          // straight to what needs fixing, when something does
          to: { name: 'settings', query: pending ? { tab: pending.tab } : {} },
          linkLabel: 'Settings'
        })
      }

      return cards
    },
    groups() {
      return [
        { key: 'week', title: 'Last week', note: rangeLabel(this.window), cards: this.weekCards },
        { key: 'now', title: 'Right now', cards: this.nowCards }
      ].filter((group) => group.cards.length)
    }
  },
  methods: {
    ...mapActions('home', ['summaryLoad']),
    /**
     * One part per integration, for the line under the count.
     *
     * SCIM and SAML are sold per workspace, and a workspace without them has no tab to send
     * anyone to - naming them would describe a switch nobody can reach, so they are left
     * out. The server reports the setting either way; whether the workspace has the feature
     * rides on the user.
     */
    integrationParts({ scim, saml, audit }) {
      const user = (this.$store.state.auth && this.$store.state.auth.user) || {}
      const part = (name, on, tab) => ({ text: `${name} ${on ? 'on' : 'off'}`, on, tab })
      const parts = []

      if (user.scim_sync_enabled) parts.push(part('SCIM', scim, 'scim'))
      if (user.saml_auth_enabled) parts.push(part('SAML', saml, 'saml'))

      // audit switched on with nowhere confirmed to send to reports to nobody
      const auditPending = audit.enabled && !audit.email_confirmed
      parts.push(auditPending
        ? { text: 'audit email not confirmed', on: true, alert: true, tab: 'audit' }
        : part('Audit', audit.enabled, 'audit'))

      return parts
    },
    directoryLine({ source, last_sync_at: at }) {
      const scim = source === 'scim'
      if (!at) return scim ? 'never synced' : 'never imported'

      return `${scim ? 'last SCIM sync' : 'last import'}: ${dayjs(at).format(DATE_LABEL_FORMAT)}`
    },
    ...mapActions('desk', ['desksFreeSearch']),
    async loadSummary() {
      const response = await this.summaryLoad({
        params: {
          since: this.window.from.format(SERVER_DATE_TIME_FORMAT),
          until: this.window.to.format(SERVER_DATE_TIME_FORMAT)
        }
      })
      this.summary = response.data
    },
    /**
     * Desks free between now and the next hour.
     *
     * Its own request on purpose: the answer is out of date in minutes, and putting it in
     * the cached summary would either hold the rest of the numbers to its pace or hand out
     * a "free right now" that is nothing of the sort.
     */
    async loadFreeNow() {
      const now = dayjs()
      const response = await this.desksFreeSearch({
        params: {
          filters: {
            since: now.format(SERVER_DATE_TIME_FORMAT),
            until: now.add(FREE_WINDOW_HOURS, 'hour').format(SERVER_DATE_TIME_FORMAT)
          },
          page: 1,
          'per-page': 1
        }
      })
      this.freeNow = response.data.pager.totalCount
    },
    async load() {
      try {
        await this.loadSummary()
      } catch (e) {
        // Api toasts the failure; with no sections there are no cards, and the personal
        // panel above this one still stands
        this.summary = {}
        return
      }

      if (!this.summary.desks) return

      try {
        await this.loadFreeNow()
      } catch (e) {
        // the desks card is worth showing without its last line
        this.freeNow = null
      }
    }
  },
  created() {
    this.load()
  }
}
</script>

<style scoped lang="scss">
// the groups sit side by side once the card is wide enough for both
.home-ws {
  flex-flow: row wrap;
  overflow: hidden;

  &__group {
    flex: 1 1 360px;
    min-width: 0;
    padding: 1rem 1.25rem 0.5rem;
    // a line above and to the left of every group; the card clips the ones on its edge
    box-shadow: -1px -1px 0 rgba(24, 28, 33, 0.08);
  }

  &__title {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 0.75rem;
    font-size: 1rem;
    font-weight: bold;
    margin-bottom: 0.25rem;
  }

  &__note {
    font-size: 0.8rem;
    font-weight: normal;
    color: #a3a4a6;
    font-variant-numeric: tabular-nums;
  }

  // the whole line is the link: its target is named in the title on hover
  &__row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 0 0.75rem;
    padding: 0.45rem 0.5rem;
    margin: 0 -0.5rem;
    border-radius: 3px;
    color: inherit;

    &[href]:hover,
    &[href]:focus {
      background: rgba(24, 28, 33, 0.03);
      text-decoration: none;
    }

    & + & {
      box-shadow: 0 -1px 0 rgba(24, 28, 33, 0.06);
    }
  }

  &__name {
    font-weight: 500;
  }

  &__value {
    font-weight: bold;
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  &__context {
    grid-column: 1 / -1;
    font-size: 0.8rem;
    color: #a3a4a6;
  }

  &__alert {
    color: #d9534f;
    font-weight: 500;
  }
}
</style>
